import { apiEndpoints } from 'shared/model/apiEndpoints'

import {
  BranchesParams,
  BranchesResponse,
  CancelOrderRequest,
  CancelOrderResponse,
  ChangeOrderBranchRequest,
  ChangeOrderBranchResponse,
  ChangeOrderRequest,
  ChangeOrdersViewRequest,
  ChangeOrdersViewResponse,
  CompaniesResponse,
  CreateOrderFormState,
  DeliveryPriceRequest,
  DeliveryPriceResponse,
  DeliverySlotsParams,
  DeliverySlotsResponse,
  GetCompaniesParams,
  OrderResponse,
  OrdersParams,
  OrdersResponse,
  PaymentMethodsResponse,
  SearchClientRequest,
  SearchClientResponse,
  SearchProductParams,
  SearchProductResponse,
  UnviewedOrdersResponse,
  ValidateProductsRequest,
  ValidateProductsResponse,
} from 'modules/Orders/types'

import { globalAPI } from 'shared/config/redux/api/global'
import {
  GET_ORDER,
  GET_ORDERS,
  GET_UNVIEWED_ORDERS,
} from 'shared/config/redux/constants/tags'

export const ordersApi = globalAPI.injectEndpoints({
  endpoints: (build) => ({
    getOrders: build.query<OrdersResponse, { params: OrdersParams }>({
      query: ({ params }) => ({
        url: apiEndpoints.orders,
        params,
      }),
      providesTags: (_result, _error, { params }) => [
        {
          type: 'GetOrders',
          id: JSON.stringify({ ...params, limit: '10', offset: '0' }),
        },
      ],
    }),
    getCompanies: build.query<
      CompaniesResponse,
      { params: GetCompaniesParams }
    >({
      query: ({ params }) => ({
        url: apiEndpoints.companies,
        params,
      }),
    }),
    getOrder: build.query<OrderResponse, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `${apiEndpoints.orders}${orderId}`,
      }),
      providesTags: [GET_ORDER],
      keepUnusedDataFor: 0,
    }),
    getBranches: build.query<BranchesResponse, { params: BranchesParams }>({
      query: ({ params }) => ({
        url: apiEndpoints.branches,
        params,
      }),
    }),
    changeOrder: build.mutation<OrderResponse, ChangeOrderRequest>({
      query: ({ params, ...body }) => ({
        method: 'PATCH',
        url: `${apiEndpoints.orders}${params.orderId}/`,
        body,
      }),
      invalidatesTags: [GET_ORDERS, GET_ORDER],
    }),
    cancelOrder: build.mutation<CancelOrderResponse, CancelOrderRequest>({
      query: ({ params, ...body }) => ({
        url: `${apiEndpoints.orders}${params.orderId}/cancel/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_ORDER, GET_ORDERS],
    }),
    searchClient: build.mutation<SearchClientResponse, SearchClientRequest>({
      query: (body) => ({
        method: 'POST',
        url: apiEndpoints.searchClient,
        body,
      }),
    }),
    getDeliverySlots: build.query<
      DeliverySlotsResponse,
      { params: DeliverySlotsParams }
    >({
      query: ({ params }) => ({
        url: apiEndpoints.deliverySlots,
        params,
      }),
    }),
    createOrder: build.mutation<any, CreateOrderFormState>({
      query: (body) => ({
        url: apiEndpoints.orders,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_ORDERS],
    }),
    searchProduct: build.query<
      SearchProductResponse,
      { params: SearchProductParams }
    >({
      query: ({ params }) => ({
        url: apiEndpoints.searchProduct,
        params,
      }),
    }),
    getPaymentMethods: build.query<PaymentMethodsResponse, void>({
      query: () => ({
        url: apiEndpoints.paymentMethods,
      }),
    }),
    getDeliveryPrice: build.mutation<
      DeliveryPriceResponse,
      DeliveryPriceRequest
    >({
      query: (body) => ({
        url: apiEndpoints.deliveryPrice,
        method: 'POST',
        body,
      }),
    }),
    validateProduts: build.mutation<
      ValidateProductsResponse,
      ValidateProductsRequest
    >({
      query: (body) => ({
        url: apiEndpoints.validateProducts,
        method: 'POST',
        body,
      }),
    }),
    changeOrderBranch: build.mutation<
      ChangeOrderBranchResponse,
      ChangeOrderBranchRequest
    >({
      query: ({ params, ...body }) => ({
        url: `${apiEndpoints.orders}${params.orderId}/changeBranch/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_ORDER],
    }),
    changeOrdersView: build.mutation<
      ChangeOrdersViewResponse,
      ChangeOrdersViewRequest
    >({
      query: (body) => ({
        url: apiEndpoints.changeOrdersView,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_UNVIEWED_ORDERS],
    }),
    getUnviewedOrders: build.query<UnviewedOrdersResponse, void>({
      query: () => ({
        url: apiEndpoints.unviewedOrders,
      }),
      providesTags: [GET_UNVIEWED_ORDERS],
    }),
  }),
})

export const {
  useLazyGetOrdersQuery,
  useGetCompaniesQuery,
  useGetOrderQuery,
  useLazyGetBranchesQuery,
  useChangeOrderMutation,
  useSearchClientMutation,
  useGetDeliverySlotsQuery,
  useLazySearchProductQuery,
  useCreateOrderMutation,
  useGetPaymentMethodsQuery,
  useGetDeliveryPriceMutation,
  useCancelOrderMutation,
  useValidateProdutsMutation,
  useChangeOrderBranchMutation,
  useChangeOrdersViewMutation,
  useGetUnviewedOrdersQuery,
} = ordersApi
